import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCheck, Clock, AlertTriangle, CreditCard, Package, ListChecks } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

const TYPE_META = {
  task_assigned:      { icon: ListChecks,    color: '#3A8C8C' },
  task_overdue:       { icon: AlertTriangle, color: '#C4727F' },
  task_due_soon:      { icon: Clock,         color: '#C67D5B' },
  payment_overdue:    { icon: CreditCard,    color: '#C4727F' },
  payment_due_soon:   { icon: CreditCard,    color: '#C67D5B' },
  delivery_delayed:   { icon: Package,       color: '#C4727F' },
  delivery_due_soon:  { icon: Package,       color: '#C67D5B' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const past = diffMs >= 0;
  const abs = Math.abs(diffMs);
  const mins = Math.floor(abs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${past ? '' : 'in '}${mins}m${past ? ' ago' : ''}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${past ? '' : 'in '}${hrs}h${past ? ' ago' : ''}`;
  const days = Math.floor(hrs / 24);
  return `${past ? '' : 'in '}${days}d${past ? ' ago' : ''}`;
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread_count: 0, last_seen_at: '' });
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications');
      setData(res.data || { items: [], unread_count: 0, last_seen_at: '' });
    } catch (e) {
      // silent — bell stays usable
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial poll + every 60s
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  // Outside-click close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const handleTogglePanel = () => {
    setOpen((v) => !v);
    if (!open) load();
  };

  const handleItemClick = async (n) => {
    if (!n.computed) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* noop */ }
    }
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
    load();
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      load();
    } catch (e) { /* noop */ }
  };

  const handleDismiss = async (e, n) => {
    e.stopPropagation();
    if (n.computed) return; // computed alerts can't be dismissed
    try {
      await api.delete(`/notifications/${n.id}`);
      load();
    } catch (e2) { /* noop */ }
  };

  const lastSeen = data.last_seen_at ? new Date(data.last_seen_at).getTime() : 0;
  const isUnread = (n) => {
    if (n.computed) {
      const c = n.created_at ? new Date(n.created_at).getTime() : 0;
      return c > lastSeen;
    }
    return !n.read;
  };

  return (
    <div className="relative" data-testid="notifications-root">
      <button
        ref={btnRef}
        onClick={handleTogglePanel}
        className="relative p-2 rounded-xl hover:bg-[#E8E6E1]/50 transition-colors"
        data-testid="notifications-btn"
        aria-label="Notifications"
      >
        <Bell size={20} strokeWidth={1.5} className="text-[#6B7280]" />
        {data.unread_count > 0 && (
          <Badge
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 text-[10px] bg-[#C67D5B] border-2 border-[#FAFAF8]"
            data-testid="notifications-badge"
          >
            {data.unread_count > 99 ? '99+' : data.unread_count}
          </Badge>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 w-[360px] max-h-[520px] bg-white rounded-2xl border border-[#E8E6E1] shadow-xl z-50 flex flex-col"
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b border-[#E8E6E1] flex items-center justify-between">
            <div>
              <h3 className="section-heading text-sm font-semibold font-['Manrope']">Notifications</h3>
              <p className="text-[10px] text-[#6B7280] mt-0.5">
                {data.unread_count > 0 ? `${data.unread_count} unread` : 'All caught up'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {data.items.length > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="p-1.5 rounded-lg hover:bg-[#E8E6E1]/50 text-[#6B7280] hover:text-[#3A8C8C] transition-colors"
                  title="Mark all as read"
                  data-testid="notifications-mark-all-read"
                >
                  <CheckCheck size={14} strokeWidth={1.5} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-[#E8E6E1]/50 text-[#6B7280] transition-colors"
                data-testid="notifications-close"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1" data-testid="notifications-list">
            {loading && data.items.length === 0 && (
              <div className="px-4 py-12 text-center text-xs text-[#6B7280]">Loading...</div>
            )}
            {!loading && data.items.length === 0 && (
              <div className="px-4 py-12 text-center" data-testid="notifications-empty">
                <Bell size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
                <p className="text-xs text-[#6B7280]">No notifications yet</p>
              </div>
            )}
            {data.items.map((n) => {
              const meta = TYPE_META[n.type] || { icon: Bell, color: '#6B7280' };
              const Icon = meta.icon;
              const unread = isUnread(n);
              return (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleItemClick(n)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleItemClick(n); } }}
                  className={`w-full text-left px-4 py-3 border-b border-[#E8E6E1] last:border-b-0 hover:bg-[#FAFAF8] transition-colors flex gap-3 group cursor-pointer ${unread ? 'bg-[#3A8C8C]/[0.03]' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${meta.color}15` }}
                  >
                    <Icon size={14} strokeWidth={1.5} style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-xs truncate ${unread ? 'font-semibold text-[#2C2C2E]' : 'font-medium text-[#6B7280]'}`}>
                        {n.title}
                      </p>
                      {unread && <span className="w-1.5 h-1.5 rounded-full bg-[#3A8C8C] shrink-0 mt-1.5" data-testid="notification-unread-dot" />}
                    </div>
                    {n.body && <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-[#D4D0C8] mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.computed && (
                    <button
                      onClick={(e) => handleDismiss(e, n)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#E8E6E1] text-[#6B7280] hover:text-[#C4727F] transition-all self-start"
                      title="Dismiss"
                      data-testid={`notification-dismiss-${n.id}`}
                    >
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
